import Link from "next/link";
import ImageComponent from "../ImageComponent";
import ReadMore from "../ReadMore/ReadMoreIndex";

export type ProjectStatItem = {
  key?: string;
  title?: string;
  description?: string;
};

export type ProjectLink = {
  label?: string;
  link?: string;
};

export type ProjectDetailsCardProps = {
  id?: string;
  title?: string;
  description?: string;
  image?: { url?: string; alt?: string } | null;
  features?: string[];
  stats?: ProjectStatItem[];
  projectLink?: ProjectLink;
  caseStudyLink?: ProjectLink;
  /** Preload when this card is likely LCP (e.g. first above-the-fold project). */
  imgPriority?: boolean;
};

const ProjectDetailsCard = ({
  title,
  description,
  image,
  features = [],
  stats = [],
  projectLink,
  caseStudyLink,
  imgPriority = false,
}: ProjectDetailsCardProps) => {
  const filteredFeatures = features.filter(Boolean);
  const filteredStats = stats.filter(
    (item) => item.title?.trim() || item.description?.trim()
  );

  // Full-card click: prefer case study, fall back to project link
  const cardLink = caseStudyLink?.link?.trim()
    ? caseStudyLink
    : projectLink;
  const cardHref = cardLink?.link?.trim() || "";
  const hasCardLink = Boolean(cardHref);
  const cardIsExternal = cardHref.startsWith("http");

  const projectHref = projectLink?.link?.trim() || "";
  const projectLabel = projectLink?.label?.trim() || "";
  const showProjectLink = Boolean(projectHref && projectLabel);
  const projectIsExternal = projectHref.startsWith("http");

  const caseStudyHref = caseStudyLink?.link?.trim() || "";
  const caseStudyLabel = caseStudyLink?.label?.trim() || "";
  const showCaseStudyLink = Boolean(caseStudyHref && caseStudyLabel);
  const caseStudyIsExternal = caseStudyHref.startsWith("http");

  const showFooterLinks = showProjectLink || showCaseStudyLink;
  return (
    <div className="card-hover group common_border relative overflow-hidden bg-surface h-full flex flex-col justify-between">
      {hasCardLink ? (
        <Link
          href={cardHref}
          target={cardIsExternal ? "_blank" : "_self"}
          rel={cardIsExternal ? "noopener noreferrer" : undefined}
          className="absolute inset-0 z-0"
          aria-label={cardLink?.label?.trim() || title || "View project"}
        />
      ) : null}
      {image?.url ? (
        <div className="relative z-1 h-auto overflow-hidden pointer-events-none [&_img]:aspect-16/10! [&_img]:object-contain! [&_img]:object-[center_18%]!">
          <ImageComponent
            url={image.url}
            img_title={image.alt || title || "Project image"}
            fullWidthNaturalHeight
            hoverScale
            groupHover
            object_contain
            width={816}
            height={1020}
            sizes="(min-width: 1280px) 380px, (min-width: 768px) 40vw, min(360px, 90vw)"
            imgPriority={imgPriority}
          />
        </div>
      ) : null}
      <div className="relative z-1 p-3 sm:p-6 flex flex-col justify-between flex-1 gap-2 sm:gap-4.5 pointer-events-none">
        <div className="space-y-1 sm:space-y-3">
          {filteredFeatures.length > 0 ? (
            <div className="hidden sm:flex gap-2 flex-wrap">
              {filteredFeatures.map((feature, index) =>
                index % 2 === 0 ? (
                  <div
                    key={`${feature}-${index}`}
                    className="rounded-full text-primary text-[11px] uppercase bg-[#d62a300f] border border-primary px-3 py-2 font-semibold"
                  >
                    {feature}
                  </div>
                ) : (
                  <div
                    key={`${feature}-${index}`}
                    className="rounded-full text-secondary text-[11px] uppercase bg-background border border-lightBrown px-3 py-2 font-semibold"
                  >
                    {feature}
                  </div>
                )
              )}
            </div>
          ) : null}
          {title?.trim() ? <h4 className="sub_heading">{title.trim()}</h4> : null}
          {description?.trim() ? (
            <>
              <ReadMore
                className="text_summary md:hidden pointer-events-auto relative z-2"
                text={description.trim()}
                showlength={{ default: 140, sm: 160 }}
                toggleClassName="cursor-pointer text-primary font-semibold"
              />
              <p className="text_summary hidden md:block">{description.trim()}</p>
            </>
          ) : null}
        </div>
        <div className="footer_card border-t border-lightBrown">
          {filteredStats.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 py-3 gap-y-2">
              {filteredStats.map((curr, index) => {
                const isLastOddOnMobile =
                  filteredStats.length % 2 === 1 &&
                  index === filteredStats.length - 1;

                return (
                  <div
                    key={curr.key ?? `${curr.title}-${index}`}
                    className={`text-center ${
                      isLastOddOnMobile ? "col-span-2 sm:col-span-1" : "col-span-1"
                    }`}
                  >
                    <h4 className="heading_text_style">{curr.title?.trim()}</h4>
                    <p className="text-xs">{curr.description?.trim()}</p>
                  </div>
                );
              })}
            </div>
          ) : null}
          {showFooterLinks ? (
            <div className="flex flex-wrap justify-between gap-4 text-primary font-bold text-xs sm:text-sm">
              {showProjectLink ? (
                <Link
                  href={projectHref}
                  target={projectIsExternal ? "_blank" : "_self"}
                  rel={projectIsExternal ? "noopener noreferrer" : undefined}
                  className="pointer-events-auto relative z-2"
                >
                  {projectLabel}
                </Link>
              ) : null}
              {showCaseStudyLink ? (
                <Link
                  href={caseStudyHref}
                  target={caseStudyIsExternal ? "_blank" : "_self"}
                  rel={caseStudyIsExternal ? "noopener noreferrer" : undefined}
                  className="pointer-events-auto relative z-2"
                >
                  {caseStudyLabel}
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailsCard;
